import React, { Fragment, useEffect, useState } from 'react';
import { Container, Row, Col, Card, CardHeader, CardBody, Table, Button, Spinner,
         UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem, Badge, Input } from 'reactstrap';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import { Breadcrumbs } from '../../../AbstractElements';
import { getInvoices, deleteInvoice, describeError, formatInr } from './purchasesApi';

const PurchaseInvoices = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');

  const load = () => {
    setLoading(true);
    getInvoices()
      .then((d) => { setInvoices(d.invoices); setError(null); })
      .catch((e) => setError(describeError(e, 'Could not load purchase invoices.')))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const handleDelete = async (inv) => {
    const confirm = await Swal.fire({
      title: `Delete ${inv.pi_ref}?`,
      html: `This removes its <strong>${inv.line_count}</strong> cost lines.<br/><br/>`
          + 'Any product whose latest cost came from this PI falls back to the PI before it.',
      icon: 'warning', showCancelButton: true, confirmButtonText: 'Delete', confirmButtonColor: '#dc3545',
    });
    if (!confirm.isConfirmed) return;
    try {
      await deleteInvoice(inv.id);
      load();
    } catch (e) {
      Swal.fire({ title: 'Could not delete', text: describeError(e), icon: 'error' });
    }
  };

  const q = search.trim().toLowerCase();
  const shown = invoices.filter((i) => !q || i.pi_ref.toLowerCase().includes(q) || String(i.pi_date).includes(q));

  return (
    <Fragment>
      <Breadcrumbs parent="Purchases" title="All PIs" mainTitle="All PIs" />
      <Container fluid>
        <Row><Col sm="12"><Card>
          <CardHeader className="d-flex justify-content-between align-items-center py-3">
            <h5 className="mb-0">Purchase invoices</h5>
            <div className="d-flex align-items-center gap-2">
              <Input type="search" value={search} placeholder="Search PI or date"
                     onChange={(e) => setSearch(e.target.value)} style={{ width: 'auto', minWidth: 200 }} />
              <Button color="primary" onClick={() => navigate('/purchases/upload')}>Upload PI</Button>
            </div>
          </CardHeader>
          <CardBody>
            {loading ? <div className="text-center p-4"><Spinner /></div>
             : error ? (
               <div className="text-center p-4">
                 <p className="text-danger mb-2">{error}</p>
                 <Button size="sm" color="secondary" onClick={load}>Try again</Button>
               </div>
             ) : shown.length === 0 ? (
               <div className="text-center p-4 text-muted">
                 {invoices.length === 0
                   ? <>No purchase invoices yet. <Button size="sm" color="link" onClick={() => navigate('/purchases/upload')}>Upload one</Button></>
                   : 'No invoices match this search.'}
               </div>
             ) : (
              <div className="table-responsive">
                <Table className="table-bordered">
                  <thead>
                    <tr>
                      <th>PI</th><th>Date</th><th className="text-end">Lines</th><th className="text-end">Styles</th>
                      <th className="text-end">Unmatched</th><th className="text-end">Avg cost</th>
                      <th className="text-end">Range</th><th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map((inv) => (
                      <tr key={inv.id}>
                        <td><strong>{inv.pi_ref}</strong></td>
                        <td>{inv.pi_date}</td>
                        <td className="text-end">{inv.line_count}</td>
                        <td className="text-end">{inv.style_count}</td>
                        <td className="text-end">
                          {Number(inv.unmatched_count) > 0
                            ? <Badge color="warning" pill>{inv.unmatched_count}</Badge>
                            : <span className="text-muted">—</span>}
                        </td>
                        <td className="text-end">{formatInr(inv.avg_cost)}</td>
                        <td className="text-end"><small className="text-muted">{formatInr(inv.min_cost)} – {formatInr(inv.max_cost)}</small></td>
                        <td>
                          <UncontrolledDropdown>
                            <DropdownToggle caret color="primary" size="sm" outline>Actions</DropdownToggle>
                            {/* Rendered into <body> so the menu isn't clipped by the table's own overflow. */}
                            <DropdownMenu end container="body">
                              <DropdownItem onClick={() => navigate(`/purchases/invoices/${inv.id}`)}>
                                <i className="fa fa-eye me-2"></i>View lines
                              </DropdownItem>
                              <DropdownItem divider />
                              <DropdownItem className="text-danger" onClick={() => handleDelete(inv)}>
                                <i className="fa fa-trash me-2"></i>Delete
                              </DropdownItem>
                            </DropdownMenu>
                          </UncontrolledDropdown>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </CardBody>
        </Card></Col></Row>
      </Container>
    </Fragment>
  );
};

export default PurchaseInvoices;
